export interface FieldMappingRule {
  fieldName: string;
  patterns: RegExp[];
  keywords: string[];
  priority: number;
  validators?: ((value: string) => boolean)[];
  transformers?: ((value: string) => string)[];
}

export interface FieldMapping {
  formFieldName: string;
  extractedFieldName: string;
  confidence: number;
  isManualMapping: boolean;
  isSelected: boolean;
  conflictResolution?: 'overwrite' | 'skip' | 'merge' | 'ask';
}

export interface FormPopulationSettings {
  mappings: FieldMapping[];
  conflictStrategy: 'ask' | 'overwrite' | 'skip';
  minimumConfidence: number;
  preserveExistingData: boolean;
}

export interface PopulationResult {
  success: boolean;
  populatedFields: string[];
  skippedFields: string[];
  conflicts: string[];
  statistics: {
    total: number;
    populated: number;
    skipped: number;
    conflicts: number;
  };
}

export interface PatternRecognitionConfig {
  vehicleRegistration: {
    patterns: RegExp[];
    keywords: string[];
  };
  phoneNumber: {
    patterns: RegExp[];
    keywords: string[];
  };
  email: {
    patterns: RegExp[];
    keywords: string[];
  };
  date: {
    patterns: RegExp[];
    keywords: string[];
  };
  name: {
    patterns: RegExp[];
    keywords: string[];
  };
  address: {
    patterns: RegExp[];
    keywords: string[];
  };
  vehicleMake: {
    patterns: RegExp[];
    keywords: string[];
    knownValues: string[];
  };
  amount: {
    patterns: RegExp[];
    keywords: string[];
  };
}

export const DEFAULT_PATTERN_CONFIG: PatternRecognitionConfig = {
  vehicleRegistration: {
    patterns: [
      /\b[A-Z]{2}[-\s]?\d{1,2}[-\s]?[A-Z]{1,2}[-\s]?\d{4}\b/g, // Standard Indian format
      /\b[A-Z]{2}\d{2}[A-Z]{2}\d{4}\b/g, // Compact format
    ],
    keywords: ['registration', 'reg no', 'vehicle no', 'number plate']
  },
  phoneNumber: {
    patterns: [
      /\b(?:\+91[-\s]?)?[6-9]\d{9}\b/g, // Indian mobile
      /\b\d{3}[-\s]?\d{3}[-\s]?\d{4}\b/g, // General format
    ],
    keywords: ['mobile', 'phone', 'contact', 'cell']
  },
  email: {
    patterns: [
      /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g
    ],
    keywords: ['email', 'e-mail', 'mail']
  },
  date: {
    patterns: [
      /\b\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}\b/g, // DD/MM/YYYY or MM/DD/YYYY
      /\b\d{2,4}[\/\-\.]\d{1,2}[\/\-\.]\d{1,2}\b/g, // YYYY/MM/DD
    ],
    keywords: ['date', 'dob', 'birth', 'issue', 'start', 'end', 'expiry']
  },
  name: {
    patterns: [
      /\b[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\b/g, // Title case names
    ],
    keywords: ['name', 'customer', 'owner', 'nominee', 'policyholder']
  },
  address: {
    patterns: [
      /\b\d+[A-Za-z]*[\s,]+[A-Za-z\s,]+(?:road|street|avenue|lane|block|sector|area|nagar|colony|garden|park|phase|extension)\b/gi,
    ],
    keywords: ['address', 'residence', 'location', 'place']
  },
  vehicleMake: {
    patterns: [
      /\b(?:maruti|hyundai|tata|mahindra|honda|ford|toyota|chevrolet|renault|nissan|volkswagen|skoda|kia|mg|jeep|bmw|mercedes|audi)\b/gi
    ],
    keywords: ['make', 'brand', 'manufacturer', 'company'],
    knownValues: ['MARUTI SUZUKI', 'HYUNDAI', 'TATA', 'MAHINDRA', 'HONDA', 'FORD', 'TOYOTA', 'CHEVROLET', 'RENAULT', 'NISSAN', 'VOLKSWAGEN', 'SKODA', 'KIA', 'MG', 'JEEP', 'BMW', 'MERCEDES-BENZ', 'AUDI']
  },
  amount: {
    patterns: [
      /\b(?:Rs\.?|INR|₹)?\s*\d+(?:,\d{3})*(?:\.\d{2})?\b/g,
      /\b\d+(?:,\d{3})*(?:\.\d{2})?\s*(?:Rs\.?|INR|₹|rupees?)\b/gi
    ],
    keywords: ['amount', 'premium', 'sum', 'cost', 'price', 'fee']
  }
};